import { advertiserRequests } from '@/mocks/adMarketplace';

const activeListings = advertiserRequests.filter(r => r.status === 'nueva' || r.status === 'analizando');
const totalBudget = activeListings.reduce((sum, r) => sum + r.budget, 0);
const categoriaCount = [...new Set(activeListings.map(r => r.category))].length;

const steps = [
  {
    icon: 'ri-megaphone-line',
    title: 'Negocios publican',
    description: 'Empresas de todos los tamaños publican sus necesidades de publicidad en radio: presupuesto, audiencia objetivo, formato preferido y duración de la campaña.',
    highlight: `${activeListings.length} campañas activas`,
  },
  {
    icon: 'ri-send-plane-line',
    title: 'Radios compiten',
    description: 'Vos ves las oportunidades, preparás tu propuesta con precio, beneficios y diferenciales. La plataforma te muestra todo lo que necesitás para armar una oferta ganadora.',
    highlight: `Presupuestos de $350 a $2,500/mes`,
  },
  {
    icon: 'ri-shake-hands-line',
    title: 'Cierran el trato',
    description: 'El anunciante elige la mejor propuesta. RFW gestiona los pagos y solo cobra una comisión cuando se concreta. Sin riesgo, sin inversión inicial.',
    highlight: `${categoriaCount} categorías disponibles`,
  },
];

export default function MarketplaceSection() {
  return (
    <section id="marketplace" className="relative py-20 md:py-28 overflow-hidden bg-background-50">
      {/* Ambient glows */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-orange-500/3 rounded-full blur-3xl pointer-events-none"></div>

      {/* Dot grid */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.02]" style={{
        backgroundImage: 'radial-gradient(circle, currentColor 1px, transparent 1px)',
        backgroundSize: '20px 20px',
      }}></div>

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 max-w-7xl mx-auto">
        {/* ── Header ── */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-50 border border-orange-200 text-orange-700 text-xs font-semibold mb-5">
            <i className="ri-store-2-line text-xs"></i>
            Marketplace RFW
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 tracking-tight leading-[1.1]">
            Negocios buscan{' '}
            <span className="text-orange-600">publicidad</span>
          </h2>
          <p className="mt-4 text-foreground-600 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            Un mercado abierto donde las marcas publican lo que necesitan y las radios compiten con sus mejores propuestas. Vos ponés el precio, el anunciante elige.
          </p>
        </div>

        {/* ── 3-step flow ── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mb-12">
          {steps.map((step, idx) => (
            <div key={step.title} className="relative flex flex-col items-center text-center">
              {/* Step number */}
              <div className="relative mb-4">
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-orange-500/10 text-orange-600 transition-all duration-300 hover:bg-orange-500/20 hover:scale-105">
                  <i className={`${step.icon} text-xl`}></i>
                </div>
                <div className="absolute -top-1 -right-1 w-6 h-6 flex items-center justify-center rounded-full bg-orange-500 text-white text-[10px] font-extrabold tabular-nums">
                  {idx + 1}
                </div>
              </div>

              <h3 className="text-foreground-950 font-bold text-sm md:text-base mb-2">{step.title}</h3>
              <p className="text-foreground-500 text-xs md:text-sm leading-relaxed mb-3">{step.description}</p>

              {/* Highlight pill */}
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-orange-50 border border-orange-200/70 text-orange-700 text-[11px] font-semibold whitespace-nowrap">
                <i className="ri-information-line text-[10px]"></i>
                {step.highlight}
              </span>

              {/* Connector arrow (between steps on desktop) */}
              {idx < steps.length - 1 && (
                <div className="hidden md:block absolute top-7 -right-8 text-orange-300">
                  <i className="ri-arrow-right-line text-2xl"></i>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* ── Stats strip ── */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 md:gap-10 p-6 rounded-2xl bg-orange-50/60 border border-orange-200/60">
          <div className="text-center">
            <div className="text-orange-600 font-extrabold text-xl md:text-2xl tabular-nums leading-tight">
              ${totalBudget.toLocaleString()}
            </div>
            <div className="text-foreground-400 text-[10px] font-medium">presupuesto mensual disponible</div>
          </div>
          <div className="hidden sm:block w-px h-8 bg-orange-200"></div>
          <div className="text-center">
            <div className="text-orange-600 font-extrabold text-xl md:text-2xl tabular-nums leading-tight">
              {activeListings.length}
            </div>
            <div className="text-foreground-400 text-[10px] font-medium">campañas activas ahora</div>
          </div>
          <div className="hidden sm:block w-px h-8 bg-orange-200"></div>
          <div className="text-center">
            <div className="text-orange-600 font-extrabold text-xl md:text-2xl tabular-nums leading-tight">
              {categoriaCount}
            </div>
            <div className="text-foreground-400 text-[10px] font-medium">categorías diferentes</div>
          </div>
          <div className="hidden sm:block w-px h-8 bg-orange-200"></div>
          <a
            href="/sa/ad-marketplace"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold cursor-pointer transition-all duration-300 hover:gap-3 whitespace-nowrap"
          >
            <span>Ver marketplace</span>
            <i className="ri-arrow-right-line text-xs"></i>
          </a>
        </div>
      </div>
    </section>
  );
}